import type AnnouncementTemplateModel from 'garaje/models/announcement-template';
import type { ReportSource } from 'garaje/models/approval-status';

export enum App {
  VISITORS = 'visitors',
  DELIVERIES = 'deliveries',
  ROOMS = 'rooms',
  DESKS = 'desks',
  WORKPLACE = 'empxp',
  PLATFORM = 'platform',
  CONNECT = 'connect',
  EMNO = 'emno',
  VFD = 'vfd',
  RESERVATIONS = 'reservations',
  SCREENS = 'screens',
}

export const APP = App;

export const ACCESS_CONTROL_INTEGRATIONS = Object.freeze({
  CISCO_MERAKI: { pluginId: '71a29345-0d89-4d3d-b7fb-758efcf99bf9', appStorePath: 'cisco-meraki' },
  BRIVO: { pluginId: 'd0e656d4-7bfc-4863-9888-1927b5dcbf94', appStorePath: `brivo` },
  KISI: { pluginId: '2797438f-226e-450b-8c2d-cb89692eb29c', appStorePath: `kisi` },
  AVIGILON_ALTA: { pluginId: '0ab9eb39-72e7-4df4-90db-1e5684c47794', appStorePath: `avigilon-alta` },
});

export const ITEM = Object.freeze({
  VISITORS: 'location',
  DELIVERIES: 'delivery area',
  ROOMS: 'room',
  DESKS: 'desk location',
});

export const PLAN_LEVEL = Object.freeze({
  BASIC: 'basic',
  STANDARD: 'standard',
  PREMIUM: 'premium',
  PREMIUM_PLUS: 'premium-plus',
  ENTERPRISE: 'enterprise',
});

export const RANK = Object.freeze({
  BASIC: 0,
  STANDARD: 10,
  PREMIUM: 20,
  ENTERPRISE: 30,
});

export const PERIOD = Object.freeze({
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  YEARLY: 'yearly',
});

export const START_SUBSCRIPTION_PLAN_ID = Object.freeze({
  DELIVERIES: 'deliveries-basic-monthly-calendar-billing',
  ROOMS: 'rooms-standard-monthly-calendar-billing',
  VISITORS: 'visitors-premium-monthly-calendar-billing',
});

export const SUBSCRIPTION_STATUS = Object.freeze({
  ACTIVE: 'active',
  IN_TRIAL: 'in_trial',
  CANCELLED: 'cancelled',
  PAUSED: 'paused',
  NON_RENEWING: 'non_renewing',
});

export enum FlowType {
  GLOBAL_CHILD = 'Flows::GlobalChild',
  EMPLOYEE_SCREENING = 'Flows::EmployeeScreening',
  PROPERTY = 'Flows::Property',
  PROPERTY_WALKUP = 'Flows::PropertyWalkUp',
  VISITOR_SCREENING = '',
}

export const FLOW_TYPE = FlowType;

export const NON_ASSIGNABLE_FLOWS = Object.freeze([FLOW_TYPE.EMPLOYEE_SCREENING, FLOW_TYPE.PROPERTY_WALKUP]);

export type NonAssignableFlow = (typeof NON_ASSIGNABLE_FLOWS)[number];

export const DESK_ATTRIBUTES_TO_UPDATE_RESOURCE_OVERVIEW = ['name', 'assignedTo', 'enabled'];

export const NON_POINTS_OF_INTEREST = Object.freeze(['desk', 'room', 'visitor-area', 'delivery-area', 'parking']);

export const DEFAULT_FLOW_NAME = Object.freeze({
  EMPLOYEE_SCREENING: 'Employee registration',
  VISITOR_SCREENING: 'Visitors',
  EMPLOYEES_EXPECTED: 'Employees expected',
});

export enum SignInFieldKind {
  SINGLE_SELECTION = 'single-selection',
  MULTIPLE_SELECTION = 'multiple-selection',
  TEXT = 'text',
  EMAIL = 'email',
  PHONE = 'phone',
  DATE = 'date',
}
export const SIGN_IN_FIELD_KINDS = SignInFieldKind;

export enum DateFieldFormat {
  MM_DD_YYYY = 'MM/DD/YYYY',
  DD_MM_YYYY = 'DD/MM/YYYY',
  YYYY_MM_DD = 'YYYY-MM-DD',
  DD_DOT_MM_YYYY = 'DD.MM.YYYY',
}

export const DATE_FIELD_FORMAT_OPTIONS = [
  { value: DateFieldFormat.MM_DD_YYYY, label: 'MM/DD/YYYY' },
  { value: DateFieldFormat.DD_MM_YYYY, label: 'DD/MM/YYYY' },
  { value: DateFieldFormat.YYYY_MM_DD, label: 'YYYY-MM-DD' },
  { value: DateFieldFormat.DD_DOT_MM_YYYY, label: 'DD.MM.YYYY' },
];

export const IFRAME_STATE = Object.freeze({
  LOADING: 'loading',
  LOADED: 'loaded',
  TIMEOUT: 'timeout',
});

export const TRUTHY = Object.freeze({
  YES: 'Yes',
  NO: 'No',
});

export const GROUP_OPERATION_OPTIONS = Object.freeze({
  AND: 'and',
  OR: 'or',
});

export const RULE_OPERATION_OPTIONS = Object.freeze({
  IS: 'is',
  IS_NOT: 'is not',
});

export const RULE_ACTION_OPTIONS = Object.freeze({
  DENY: 'deny_entry',
  ALERT: 'alert',
  REQUIRE_APPROVAL: 'require_approval',
});

export const DISCARDED_RESPONSE_STRINGS = Object.freeze({
  PLACEHOLDER: '::RESPONSE_DISCARDED::',
  DISPLAY_TEXT: 'Discarded',
});

export const STORE_RESPONSE_CONFIG_OPTIONS = Object.freeze({
  STORED: 'stored',
  DISCARDED: 'discarded',
  MANAGED: 'managed',
});

export const NEIGHBORHOOD_SCIM_CATEGORIES = Object.freeze({
  DEPARTMENTS: 'Departments',
  DIVISIONS: 'Divisions',
  ORGANIZATIONS: 'Organizations',
  GROUPS: 'Groups',
});

export const PURPOSE_OF_VISIT = Object.freeze({
  EMPLOYEE_REGISTRATION: 'Employee registration',
});

export const BANNERS = Object.freeze({
  UPGRADE_REQUIRED: 'upgrade-required',
});

export const LANGUAGE_SUPPORT = Object.freeze({
  DEFAULT: 'en',
});

export const DESK_AVAILABILITY = Object.freeze({
  AVAILABLE: 'available',
  UNAVAILABLE: 'unavailable',
  USER_ASSIGNED: 'userAssigned',
});

export const APPROVAL_STATUS = Object.freeze({
  APPROVED: 'approved',
  DENIED: 'denied',
  PENDING: 'pending',
  REVIEW: 'review',
});

export const INVITE_STATE = Object.freeze({
  APPROVED: 'approved',
  DENIED: 'denied',
  PAST_WINDOW: 'pastWindow',
  SIGNED_IN: 'signedIn',
  SCHEDULED: 'scheduled',
  UNSCHEDULED: 'unscheduled',
  PENDING: 'pending',
});

export const LENDING_STATUS = Object.freeze({
  NOT_LENT: 'NOT_LENT',
  LENT_AVAILABLE: 'LENT_AVAILABLE',
  LENT_BOOKED: 'LENT_BOOKED',
});

export const DESK_COUNT = Object.freeze({
  PER_LICENSE: 25,
  WARNING_THRESHOLD: 6,
});

export const EMPLOYEE_COUNT = Object.freeze({
  EMPLOYEE_LIMIT: 50,
});

export const IMPRESSION_NAMES = Object.freeze({
  CHILI_PIPER_MEETING_URL_OPENED: 'chili-piper-meeting-redirect-opened',
  CHILI_PIPER_MODAL_OPENED: 'chili-piper-modal-opened',
  EMPLOYEE_GATING: 'employee-gating',
  LOCATION_GATING_MODAL_DEACTIVATE_LOCATIONS_CLICKED: Object.freeze({
    BASIC: 'location_gating_modal_deactivate_locations_clicked_on_basic',
    PREMIUM: 'location_gating_modal_deactivate_locations_clicked_on_upgrade_to_premium',
    STANDARD: 'location_gating_modal_deactivate_locations_clicked_on_upgrade_to_standard',
  }),
  LOCATION_GATING_MODAL_DEACTIVATE_LOCATIONS_SHOWN: Object.freeze({
    BASIC: 'location_gating_modal_deactivate_locations_shown_on_basic',
    PREMIUM: 'location_gating_modal_deactivate_locations_shown_on_premium',
    STANDARD: 'location_gating_modal_deactivate_locations_shown_on_standard',
  }),
  LOCATION_GATING_MODAL_DEACTIVATE_LOCATIONS_SHOWN_ON_UPGRADE: Object.freeze({
    PREMIUM: 'location_gating_modal_deactivate_locations_shown_on_upgrade_to_premium',
    STANDARD: 'location_gating_modal_deactivate_locations_shown_on_upgrade_to_standard',
  }),
  LOCATION_GATING_MODAL_GET_MORE_LOCATIONS_CLICKED: Object.freeze({
    BASIC: 'location_gating_modal_get_more_locations_from_basic_clicked',
    PREMIUM: 'location_gating_modal_get_more_locations_from_premium_clicked',
    STANDARD: 'location_gating_modal_get_more_locations_from_standard_clicked',
  }),
  LOCATION_GATING_MODAL_SHOWN: Object.freeze({
    BASIC: 'location_gating_modal_shown_on_basic',
    NON_ADMIN: 'location_gating_modal_shown_on_non_admin',
    PREMIUM: 'location_gating_modal_shown_on_premium',
    STANDARD: 'location_gating_modal_shown_on_standard',
  }),
  LOCATION_GATING_MODAL_SHOWN_ON_UPGRADE: Object.freeze({
    NON_ADMIN: 'location_gating_modal_shown_on_non_admin',
    PREMIUM: 'location_gating_modal_shown_on_upgrade_to_premium',
    STANDARD: 'location_gating_modal_shown_on_upgrade_to_standard',
  }),
  LOCATION_GATING_MODAL_UPGRADE_CLICKED: Object.freeze({
    BASIC: 'location_gating_modal_upgrade_from_basic_clicked',
    PREMIUM: 'location_gating_modal_upgrade_to_premium_clicked',
    STANDARD: 'location_gating_modal_upgrade_to_standard_clicked',
  }),
  PREMIUM_FEATURE_GATING_MODAL_SHOWN: 'premium-feature-gating-modal-shown',
  VISITORS_BASIC_TO_PREMIUM_TRIAL: 'visitors-basic-to-premium-trial',
  VISITORS_ONBOARDING_FIRST_VIEW: 'visitors-onboarding-first-view',
  VISITORS_ONBOARDING_IPAD_NO: 'visitors-onboarding-ipad-no',
  VISITORS_ONBOARDING_IPAD_YES: 'visitors-onboarding-ipad-yes',
  VISITORS_ONBOARDING_VIDEO_WALKTHROUGH_CLOSED: 'visitors-onboarding-video-walkthrough-closed',
  VISITORS_COUPON_OFFER_MODAL_CLICKED: 'visitors-coupon-offer-modal-clicked',
  VISITORS_COUPON_OFFER_MODAL_CLOSED: 'visitors-coupon-offer-modal-closed',
  VISITORS_COUPON_OFFER_MODAL_VIEWED: 'visitors-coupon-offer-modal-viewed',
  VISITORS_TRIAL_END_MODAL: Object.freeze({
    VIEWED: 'visitors-trial-end-modal-viewed',
    CLOSED: 'visitors-trial-end-modal-closed',
    BASIC: 'visitors-trial-end-modal-clicked-on-basic',
    STANDARD: 'visitors-trial-end-modal-clicked-on-standard',
    PREMIUM: 'visitors-trial-end-modal-clicked-on-premium',
  }),
  WORKPLACE_TRIAL_END_MODAL: Object.freeze({
    VIEWED: 'workplace_trial_end_modal_viewed',
    CLOSED: 'workplace_trial_end_close_clicked',
    STANDARD: 'workplace_trial_end_standard_clicked',
    PREMIUM: 'workplace_trial_end_premium_clicked',
  }),
  WORKPLACE_FREE_BUNDLE_BANNER_DISMISSED: 'workplace-free-bundle-banner-dismissed',
  NAVATTIC_IPAD_DEMO_VIEWED: 'navattic-ipad-demo-viewed',
  CONTACT_BDR_MODAL_SHOWN: 'contact-bdr-modal-shown',
  CONTACT_BDR_MODAL_SHOWN_BASIC: 'contact-bdr-modal-shown-on-basic',
  BILLING_CHANGE_PLANS_PAGE_VIEWED: 'billing-change-plans-page-viewed',
  BILLING_PLANS_PAGE_VIEWED: 'billing-plans-page-viewed',
  ADD_CREDIT_CARD_CLICKED: 'add-credit-card-clicked',
  SIGNUP_SURVEY_VIEWED: 'signup-survey-viewed',
  SIGNUP_SURVEY_CLOSED: 'signup-survey-closed',
  SIGNUP_SURVEY_SUBMITTED: 'signup-survey-submitted',
  IPAD_ALERTS_CONFIG_UPDATED: 'ipad-alerts-config-updated',
  PRINTER_ALERTS_CONFIG_UPDATED: 'printer-alerts-config-updated',
  VISTORS_BUY_NOW: Object.freeze({
    CTA_VIEWED: 'visitors-buy-now-cta-viewed',
    CTA_CLICKED: 'visitors-buy-now-cta-clicked',
    MODAL_CLOSED: 'visitors-buy-now-modal-closed',
    STANDARD_CLICKED: 'visitors-buy-now-modal-standard-clicked',
    PREMIUM_CLICKED: 'visitors-buy-now-modal-premium-clicked',
  }),
  EMERGENCY_NOTIFICATIONS: Object.freeze({
    CONFIGURE_EMERGENCY_NOTIFICATIONS_CLICKED: 'emno-configure-emno-clicked',
    CONFIGURE_EMERGENCY_NOTIFICATIONS_DISMISSED: 'emno-configure-emno-dismissed',
    PHONE_NUMBER_BANNER_CLICKED: 'emno-configure-emno-clicked',
    PHONE_NUMBER_BANNER_DISMISSED: 'emno-configure-emno-dismissed',
  }),
  DELIVERIES: Object.freeze({
    ADD_DELIVERY_AREA_CLICKED: 'add-delivery-area-clicked',
    DELIVERIES_LOG_VISITED: 'deliveries-log-visited',
  }),
  DASHBOARD: Object.freeze({
    HERO_CLOSED: 'homepage-hero-closed',
  }),
  MAP: Object.freeze({
    BULK_EDITING_TOOLTIP_DISMISSED: 'map-bulk-editing-tooltip-dismissed',
    DESK_AUTO_GENERATION_TOOLTIP_DISMISSED: 'map-desk-auto-generation-tooltip-dismissed',
    DESK_AUTO_GENERATION_ANNOUNCEMENT_BANNER_DISMISSED: 'desk-auto-generation-announcement-banner-dismissed',
  }),
  KIOSK_DEMO_IPAD_SIGNIN: 'kiosk-demo-signin-completed',
  HOST_OPTIONS_EMAIL_NOTIFICATION: Object.freeze({
    ENABLED: 'email-notification-enabled',
    DISABLED: 'email-notification-disabled',
  }),
  HOST_OPTIONS_SMS_NOTIFICATION: Object.freeze({
    ENABLED: 'sms-notification-enabled',
    DISABLED: 'sms-notification-disabled',
  }),
  HOST_OPTIONS_HOST_NOTIFICATIONS: Object.freeze({
    ENABLED: 'host-notifications-enabled',
    DISABLED: 'host-notifications-disabled',
  }),
  HOST_OPTIONS_RECEPTIONIST_NOTIFICATION: Object.freeze({
    ENABLED: 'notify-receptionists-on-host-reply-enabled',
    DISABLED: 'notify-receptionists-on-host-reply-disabled',
  }),
  SECURITY_VR_SCREENING_OPTIONS_BLOCK_LIST: Object.freeze({
    ENABLED: 'blacklist-enabled',
    DISABLED: 'blacklist-disabled',
  }),
  SECURITY_VR_SCREENING_OPTIONS_VISUAL_COMPLIANCE: Object.freeze({
    ENABLED: 'visual-compliance-enabled',
    DISABLED: 'visual-compliance-disabled',
  }),
  VR_WELCOME_SCREEN_MULTIPLE_LANGUAGES: Object.freeze({
    ENABLED: 'multiple-languages-enabled',
    DISABLED: 'multiple-languages-disabled',
  }),
  VR_INVITES_PRE_REGISTRATION: Object.freeze({
    ENABLED: 'pre-registration-enabled',
    DISABLED: 'pre-registration-disabled',
  }),
  VR_INVITES_INVITE_NOTIFICATION: Object.freeze({
    ENABLED: 'cc-receptionist-enabled',
    DISABLED: 'cc-receptionist-disabled',
  }),
  VR_INVITES_NEAR_VISIT_REGISTRATION: Object.freeze({
    ENABLED: 'near-visit-screening-enabled',
    DISABLED: 'near-visit-screening-disabled',
  }),
  VR_INVITES_PRE_REGISTRATION_REQUIRED: Object.freeze({
    ENABLED: 'pre-registration-required-enabled',
    DISABLED: 'pre-registration-required-disabled',
  }),
  VR_INVITES_TOUCHLESS_SIGN_IN: Object.freeze({
    ENABLED: 'touchless-signin-enabled',
    DISABLED: 'touchless-signin-disabled',
  }),
  VR_INVITES_SECURITY_DESK_LINK: Object.freeze({
    ENABLED: 'security-desk-link-enabled',
    DISABLED: 'security-desk-link-disabled',
  }),
  VR_EMAILS_WELCOME_GUIDE: Object.freeze({
    ENABLED: 'visitor-guide-enabled',
    DISABLED: 'visitor-guide-disabled',
  }),
  VR_EMAILS_SURVEY_EMAIL: Object.freeze({
    ENABLED: 'visitor-survey-enabled',
    DISABLED: 'visitor-survey-disabled',
  }),
  VR_NOTIFICATIONS_FALLBACK_NOTIFICATION: Object.freeze({
    ENABLED: 'fallback-notifications-enabled',
    DISABLED: 'fallback-notifications-disabled',
  }),
  VR_NOTIFICATIONS_ADDITIONAL_EMAIL_RECIPIENTS: Object.freeze({
    ENABLED: 'additional-email-recipients-enabled',
    DISABLED: 'additional-email-recipients-disabled',
  }),
  VR_NOTIFICATIONS_DEVICE_IPAD_STATUS_EMAIL: Object.freeze({
    ENABLED: 'device-notifications-enabled',
    DISABLED: 'device-notifications-disabled',
  }),
  VR_NOTIFICATIONS_DEVICE_PRINTER_STATUS_EMAIL: Object.freeze({
    ENABLED: 'printer-notifications-enabled',
    DISABLED: 'printer-notifications-disabled',
  }),
});

export const SETUP_NAMES = Object.freeze({
  SIGNUP_SURVEY_COMPLETED: 'signup-survey-completed',
});

export const TenantConnectionStatus = Object.freeze({
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  INACTIVE: 'inactive',
  PENDING: 'pending',
});

export const BlocklistFilterSource = Object.freeze({
  LOCATION: 'Location',
  PROPERTY: 'Envoy::MultiTenancy::Zone',
});

export const CsvUploadDestination = Object.freeze({
  BLOCKLIST_FILTERS: 'blacklist_filters',
});

export const CsvUploadStatus = Object.freeze({
  DONE: 'done',
  IN_PROGRESS: 'in_progress',
});

export const getEnum = (
  key: 'APP' | 'PLAN_LEVEL' | 'PLAN_RANK' | 'PLAN_PERIOD' | 'START_SUBSCRIPTION_PLAN_ID' | 'SUBSCRIPTION_STATUS',
): unknown => {
  switch (key) {
    case 'APP':
      return APP;
    case 'PLAN_LEVEL':
      return PLAN_LEVEL;
    case 'PLAN_RANK':
      return RANK;
    case 'PLAN_PERIOD':
      return PERIOD;
    case 'START_SUBSCRIPTION_PLAN_ID':
      return START_SUBSCRIPTION_PLAN_ID;
    case 'SUBSCRIPTION_STATUS':
      return SUBSCRIPTION_STATUS;
  }
};

export const SECONDS_IN_A_DAY = 86400;
export const MINUTES_IN_A_WEEK = 10080;
export const MINUTES_IN_A_DAY = 1440;

export const SLACK_V2_PLUGIN_KEY = '7828c68d-b570-4c82-9f94-149c29b659ad';
export const MSTEAMS_V2_PLUGIN_KEY = '76dfe3b3-bda0-4781-9469-fadeb6fe3f56';

export const ACS_PLUGIN_IDS = [
  '7f28f1a9-5046-4522-9d03-ca3b4cc7339c', // Access It
  '29f2e617-508d-4bc3-9363-0b06268a9f76', // AMAG
  '0ab9eb39-72e7-4df4-90db-1e5684c47794', // Openpath
  'd4cd09f7-10da-4b30-a86d-0d05066cc6d2', // Avigilon Unity Access
  'd0e656d4-7bfc-4863-9888-1927b5dcbf94', // Brivo Access
  'a38bce29-2b86-47d7-9f34-8a2184233c2f', // C-CURE 9000
  'b02c9002-a5e1-4f8f-9ed4-2952cc41d7a3', // Gallagher
  '4f03319e-3584-42f4-845b-b1105d6cf4df', // Genea
  'b4cac582-0f6e-444a-8c96-2b82a3d95b98', // Genetec Security Centereu
  '4c416333-bfd0-48ac-aaec-e9224559f1b5', // Kantech
  '2797438f-226e-450b-8c2d-cb89692eb29c', // Kisi
  'a2954e67-8c63-4f9b-bda8-cd9b84b12094', // AEOS by Nedap
  '476d5235-f2ea-4f8b-8116-734ec3c1957c', // OnGuard
  'ff7a4786-7e2e-4c69-8715-cbb3b05504c4', // Net2 by Paxton
  '2d8251f0-ca3c-4b53-93cd-d3c4b45e0a40', // Proxy
  'f03ff630-d6be-4f0d-99d9-ecf810a0a5d3', // LenelS2 Netbox
];

export const EXAMPLE_ANNOUNCEMENT_TEMPLATES: Partial<AnnouncementTemplateModel>[] = [
  {
    id: 'fire-emergency',
    name: 'Fire emergency',
    title: 'Fire Emergency Notification',
    message: `Fire emergency at [Location]. Evacuate immediately using the nearest exit. Stay clear of the building and await further instructions.`,
  },
  {
    id: 'severe-weather',
    name: 'Severe weather',
    title: 'Severe Weather Alert',
    message: `Severe weather warning: [Type of Weather] expected at [Location]. Seek shelter immediately. Follow safety protocols. Office may close early. Stay tuned for updates.`,
  },
  {
    id: 'medical-emergency',
    name: 'Medical emergency',
    title: 'Medical Emergency Alert',
    message: `Medical emergency at [Location]. Please remain calm. First responders are en route. Avoid the area to allow emergency personnel to work. Await further instructions.`,
  },
  {
    id: 'violent-incident',
    name: 'Violent incident',
    title: 'Violent Incident Alert',
    message: `Violent incident reported at [Location]. Lockdown immediately. Hide in a secure place. Silence phones. Call 911 if safe. Await further instructions.`,
  },
  {
    id: 'workplace-hazard',
    name: 'Workplace hazard',
    title: 'Workplace Hazard Notification',
    message: `Hazard reported at [Location]. Avoid the area and follow safety protocols. Do not use [specific equipment/area] until further notice. Safety team is assessing the situation. Await further instructions.`,
  },
  {
    id: 'it-system-outage',
    name: 'IT system outage',
    title: 'IT System Outage Notification',
    message: `Critical IT system outage. [System] is currently down. IT team is working to resolve the issue. Updates will follow shortly.`,
  },
  {
    id: 'power-outage',
    name: 'Power outage',
    title: 'Power Outage Notification',
    message: `Power outage at [Location]. Backup generators are active. Please save your work and power down non-essential equipment. Updates on restoration will follow.`,
  },
  {
    id: 'suspicious-activity',
    name: 'Suspicious activity',
    title: 'Suspicious Activity Alert',
    message: `Suspicious activity reported at [Location]. Initiate lockdown procedures immediately. Stay in a secure location. Avoid windows and doors. Await further instructions.`,
  },
];

export default {
  APP,
  ACCESS_CONTROL_INTEGRATIONS,
  APPROVAL_STATUS,
  BANNERS,
  DEFAULT_FLOW_NAME,
  DESK_AVAILABILITY,
  DESK_COUNT,
  FLOW_TYPE,
  NON_ASSIGNABLE_FLOWS,
  NON_POINTS_OF_INTEREST,
  GROUP_OPERATION_OPTIONS,
  IFRAME_STATE,
  ITEM,
  LANGUAGE_SUPPORT,
  NEIGHBORHOOD_SCIM_CATEGORIES,
  PERIOD,
  PLAN_LEVEL,
  PLAN_PERIOD: PERIOD,
  PLAN_RANK: RANK,
  RANK,
  RULE_ACTION_OPTIONS,
  RULE_OPERATION_OPTIONS,
  SECONDS_IN_A_DAY,
  SUBSCRIPTION_STATUS,
  getEnum,
};

export const ANNOUNCEMENT_TEMPLATE_EMPLOYEE_AUDIENCES = [
  {
    label: 'Employees checked in',
    value: 'ALL_EMPLOYEES_CHECKED_IN',
  },
  {
    label: 'Employees scheduled but not checked in ',
    value: 'ALL_EMPLOYEES_WITH_RESERVATION',
  },
  {
    label: 'Employees with primary location of send location',
    value: 'ALL_EMPLOYEES_AT_DEFAULT_LOCATION',
  },
  {
    label: 'Employee directory of send location',
    value: 'ALL_EMPLOYEES_AT_LOCATION',
  },
];

export const ANNOUNCEMENT_TEMPLATE_VISITOR_AUDIENCES = [
  {
    label: 'Visitors signed in',
    value: 'ALL_VISITORS_CHECKED_IN',
  },
  {
    label: 'Visitors invited but not signed in',
    value: 'ALL_VISITORS_WITH_RESERVATION',
  },
];

export const ANNOUNCEMENT_TEMPLATE_VISITOR_AUDIENCES_M2 = [
  {
    label: 'Visitors signed in',
    value: 'ALL_VISITORS_CHECKED_IN',
  },
  {
    label: 'Visitors invited but not signed in',
    value: 'ALL_VISITORS_WITH_RESERVATION',
  },
  {
    label: 'Visitors signed out',
    value: 'ALL_VISITORS_SIGNED_OUT',
  },
];

export const ANNOUNCEMENT_RECIPIENT_OPTIONS = [
  {
    label: 'All recipients',
    value: 'all',
  },
  {
    label: 'Employees',
    value: 'employee',
  },
  {
    label: 'Visitors',
    value: 'visitor',
  },
];

export const ANNOUNCEMENT_STATUS_OPTIONS = [
  {
    icon: undefined as undefined | string,
    label: 'All statuses',
    value: 'all',
  },
];

export const EMERGENCY_NOTIFICATION_STATUS_OPTIONS = [
  {
    label: 'All statuses',
    value: 'all',
  },
  {
    icon: 'icon-shield-light-gray',
    label: 'Not marked as safe',
    value: 'not-marked-as-safe',
  },
  {
    icon: 'icon-shield-green',
    label: 'Marked as safe',
    value: 'marked-as-safe',
  },
];

export const EMERGENCY_NOTIFICATION_STATUS_OPTIONS_2_WAY_COMMS = [
  {
    label: 'All statuses',
    value: 'all',
  },
  {
    icon: 'icon-shield-green',
    label: "I'm safe",
    value: 'safe',
  },
  {
    icon: 'icon-shield-red',
    label: 'I need help',
    value: 'need_help',
  },
  {
    icon: 'icon-shield-light-gray',
    label: 'No response yet',
    value: 'no_response',
  },
];

export type ANNOUNCEMENT_EMPLOYEE_AUDIENCE =
  | 'self'
  | 'all_employees_at_location'
  | 'all_employees_at_default_location'
  | 'all_employees_with_reservation'
  | 'all_employees_checked_in'
  | 'custom_employee_audience'
  | 'employee_group'
  | 'employees_without_phone_numbers';

export type ANNOUNCEMENT_VISITOR_AUDIENCE = 'all_visitors_with_reservation' | 'all_visitors_checked_in';

export const ANNOUNCEMENT_CHANNEL_NAME = Object.freeze({
  sms: 'SMS',
  push: 'Push',
  email: 'Email',
  slack: 'Slack',
  ms_teams: 'Microsoft Teams',
});

export const ANNOUNCEMENT_RECIPIENT_GROUP_NAME = Object.freeze({
  self: 'Just you',
  all_employees_at_location: 'Employee directory of %{location_name}',
  all_employees_at_default_location: 'Employees with primary location of %{location_name}',
  all_employees_with_reservation: 'Employees scheduled but not checked in',
  all_employees_checked_in: 'Employees checked in',
  custom_employee_audience: 'Custom employee audience',
  employee_group: 'Employee group',
  employees_without_phone_numbers: 'Employees without phone numbers',
  all_visitors_with_reservation: 'Visitors invited but not signed in',
  all_visitors_checked_in: 'Visitors signed in',
  all_visitors_signed_out: 'Visitors signed out',
});

export const ANNOUNCEMENT_RECIPIENT_FILTERS = Object.freeze({
  email_sent: 'email_sent',
  push_sent: 'push_sent',
  sms_sent: 'sms_sent',
  slack_sent: 'slack_sent',
  ms_teams_sent: 'ms_teams_sent',
  recipient_type: 'recipient_type',
  notification_status_equal_to: 'notification_status_equal_to',
  marked_as_safe: 'marked_as_safe',
  response: 'response',
});

export enum Day {
  Sunday,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
}

export const DraftSharingStrategy = Object.freeze({
  EMPLOYEES_AT_LOCATION: 'EMPLOYEES_AT_LOCATION',
  CUSTOM: 'CUSTOM',
  ANYONE_WITH_LINK: 'ANYONE_WITH_LINK',
});

export const DRAFT_SHARE_OPTIONS = [
  {
    label: 'Only people with view or edit access',
    value: DraftSharingStrategy.CUSTOM,
  },
  {
    label: 'All employees with the link',
    value: DraftSharingStrategy.EMPLOYEES_AT_LOCATION,
  },
  {
    label: 'Anyone with the link',
    value: DraftSharingStrategy.ANYONE_WITH_LINK,
    gated: true,
  },
];

export const LIVE_MAP_SHARE_STRATEGY = Object.freeze({
  INTERNAL: 'INTERNAL',
  EXTERNAL: 'EXTERNAL',
});

export const LIVE_MAP_SHARE_OPTIONS = [
  {
    label: 'Only people with view or edit access',
    value: LIVE_MAP_SHARE_STRATEGY.INTERNAL,
  },
  {
    label: 'Anyone with the link',
    value: LIVE_MAP_SHARE_STRATEGY.EXTERNAL,
  },
];

export const LIVE_MAP_SHARE_STRATEGY_OPTIONS_MAPPING = {
  [LIVE_MAP_SHARE_STRATEGY.INTERNAL]: LIVE_MAP_SHARE_OPTIONS[0],
  [LIVE_MAP_SHARE_STRATEGY.EXTERNAL]: LIVE_MAP_SHARE_OPTIONS[1],
};

export const LIVE_MAP_SHARE_INCLUDE_FIELDS = Object.freeze({
  SENSITIVE_INFO: 'pii_enabled',
});

export enum AnalyticsEventNames {
  ATTENDANCE_DASHBOARD_VISITED = 'attendance_dashboard_visited',
  ATTENDANCE_GLOBAL_DASHBOARD_VISITED = 'attendance_global_dashboard_visited',
  DESKS_DASHBOARD_VISITED = 'desks_dashboard_visited',
  DESKS_GLOBAL_DASHBOARD_VISITED = 'desks_global_dashboard_visited',
  DELIVERIES_DASHBOARD_VISITED = 'deliveries_dashboard_visited',
  DELIVERIES_GLOBAL_DASHBOARD_VISITED = 'deliveries_global_dashboard_visited',
  ROOMS_DASHBOARD_VISITED = 'rooms_dashboard_visited',
  ROOMS_GLOBAL_DASHBOARD_VISITED = 'rooms_global_dashboard_visited',
  STRATEGIC_INSIGHTS_DASHBOARD_VISITED = 'strategic_insights_dashboard_visited',
  OCCUPANCY_DASHBOARD_VISITED = 'occupancy_dashboard_visited',
  OCCUPANCY_GLOBAL_DASHBOARD_VISITED = 'occupancy_global_dashboard_visited',
  VISITORS_DASHBOARD_VISITED = 'visitors_dashboard_visited',
  VISITORS_GLOBAL_DASHBOARD_VISITED = 'visitors_global_dashboard_visited',
  BENCHMARKING_GLOBAL_DASHBOARD_VISITED = 'benchmarking_global_dashboard_visited',
  SPACE_INDEX_GLOBAL_DASHBOARD_VISITED = 'space_index_global_dashboard_visited',
  SCHEDULED_REPORTS_V2_VISITED = 'schedule_reports_v2_location_visited',
  SCHEDULED_REPORTS_V2_GLOBAL_VISITED = 'schedule_reports_v2_global_visited',
  CUSTOM_GLOBAL_DASHBOARDS_VISITED = 'custom_global_dashboards_visited',
  CUSTOM_DASHBOARDS_VISITED = 'custom_dashboards_visited',
  SCHEDULED_EXPORTS_VISITED = 'scheduled_exports_location_visited',
  SCHEDULED_EXPORTS_GLOBAL_VISITED = 'scheduled_exports_global_visited',
  PARKING_DASHBOARD_VISITED = 'parking_dashboard_visited',
  PARKING_GLOBAL_DASHBOARD_VISITED = 'parking_global_dashboard_visited',
  SCREENS_DASHBOARD_VISITED = 'screens_dashboard_visited',
  SCREENS_GLOBAL_DASHBOARD_VISITED = 'screens_global_dashboard_visited',
}

export enum SubscriptionEventNames {
  SubscriptionStatusChanged = 'subscription_status_changed',
}

export enum SubscriptionStatuses {
  TrialStarted = 'trial_started',
}

export const GENERIC_RESOURCE_TYPES = ['parking'];
export const RESOURCE_TYPE_ROOM = 'room';
export const GENERIC_RESOURCE_ID_MAPPING = {
  desks: 1,
  parking: 2,
  rooms: 3,
};

export enum GENERIC_RESOURCE_ICON {
  PARKING = '/assets/images/resource-map/parking-icon.svg',
  ROOM = '/assets/images/resource-map/room-icon.svg',
  DESK = '/assets/images/resource-map/desk-icon.svg',
  NO_ICON = '',
}

export enum RESERVATION_MODAL_PAGES {
  EMPLOYEE = 'employee',
  MAP = 'map',
  RESOURCES_MAP = 'resources-map',
  DELETE = 'delete',
  UNSAVED = 'unsaved',
  APPROVAL = 'approval',
}

export const SCREENER_RESULT_SOURCE_DISPLAY_NAMES: Readonly<Record<ReportSource, string>> = Object.freeze({
  blacklist: 'Block list match',
  'capacity-screening': 'Capacity limit check',
  'conditional-rule': 'Rule check',
  'employee-screening': 'Employee access check',
  'id-scanning': 'ID verification (via Veriff)',
  'identity-verification': 'Identity check (via integration)',
  'pre-registration-required': 'Pre-registration required',
  'property-invite-approval': 'Outgoing property invite',
  'requires-approval': 'Outgoing invite',
  'temperature-screening': 'Temperature screening',
  'visitor-documents': 'Document signing required',
  'visual-compliance': 'Visual Compliance match',
  'visit-workflows': 'Advanced Approvals',
});

export const RECURRING_OPTIONS = Object.freeze({
  THIS: 'this',
  THIS_AND_FOLLOWING: 'thisAndFollowing',
  ALL: 'all',
});
